import React, { useEffect, useState } from "react";
import { api, fmt } from "@/api";
import { JerseyBadge } from "@/components/Shared";
import { Share2, Lock } from "lucide-react";

export default function Results() {
  const [data, setData] = useState({ divisions: [] });
  const [tab, setTab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [toast, setToast] = useState("");

  const load = async () => {
    const d = await api.results();
    setData(d);
    setTab((prev) => prev || (d.divisions[0] && d.divisions[0].id));
    setLoading(false);
  };

  useEffect(() => {
    load();
    api.config().then((c) => setRevealed(!!c.results_revealed)).catch(() => {});
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, []);

  const flash = (t) => { setToast(t); setTimeout(() => setToast(""), 2200); };

  const share = async (url, title, text) => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (e) {
        if (e && e.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      flash("Link copied — paste it anywhere to share");
    } catch (e) {
      window.prompt("Copy this link to share:", url);
    }
  };

  if (loading) return <div className="p-8 text-center font-display uppercase tracking-widest text-gray-400">Loading…</div>;

  const active = data.divisions.find((d) => d.id === tab);

  return (
    <div className="max-w-3xl mx-auto p-4 pb-28" data-testid="results-view">
      <h1 className="font-display text-4xl font-black uppercase tracking-tight mb-1">Results</h1>
      <p className="font-display text-gray-500 uppercase text-xs tracking-widest mb-4">
        Heat results by round · Finals revealed at the ceremony
      </p>

      {data.divisions.length === 0 ? (
        <div className="brutal p-8 text-center" data-testid="results-empty">
          <div className="font-score text-3xl">NO RESULTS YET</div>
          <p className="font-display text-gray-500 uppercase text-xs tracking-widest mt-2">Heats will show here once scored</p>
        </div>
      ) : (
        <>
          <div className="flex gap-2 overflow-x-auto pb-2 mb-4" data-testid="results-division-tabs">
            {data.divisions.map((d) => (
              <button
                key={d.id}
                data-testid={`results-tab-${d.id}`}
                onClick={() => setTab(d.id)}
                className={`brutal-2 whitespace-nowrap px-3 py-2 font-display font-black uppercase text-xs tracking-widest ${
                  tab === d.id ? "bg-black text-white" : "bg-white"
                }`}
              >
                {d.name}
              </button>
            ))}
          </div>

          {active && (
            <button
              data-testid={`share-division-${active.id}`}
              onClick={() => share(
                api.shareDivisionUrl(active.id),
                `${active.name} — Pounders Fest`,
                revealed ? `Final standings for ${active.name}` : `Follow ${active.name} at Pounders Fest`
              )}
              className="w-full brutal bg-[#FFD700] font-display font-black uppercase tracking-widest py-3 mb-4 flex items-center justify-center gap-2 active:scale-[0.99] transition-transform"
            >
              {revealed ? <Share2 size={18} /> : <Lock size={16} />}
              {revealed ? `Share ${active.name} Standings` : "Share Division (podium hidden till ceremony)"}
            </button>
          )}

          {active &&
            active.rounds.map((rnd) => (
              <div key={rnd.id} className="mb-6" data-testid={`results-round-${rnd.id}`}>
                <h2 className="font-display text-xl font-black uppercase tracking-tight border-b-4 border-black pb-1 mb-3">
                  {rnd.name}
                </h2>
                {rnd.heats.map((h) => (
                  <div key={h.id} className="brutal p-3 mb-3" data-testid={`results-heat-${h.id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-display font-black uppercase">{h.name}</span>
                      {h.is_live && <span className="bg-[#E63946] text-white text-[10px] font-black uppercase px-2 py-0.5 tracking-widest">Live</span>}
                      <button
                        data-testid={`share-heat-${h.id}`}
                        onClick={() => share(
                          api.shareHeatUrl(h.id),
                          `${active.name} — ${h.name} · Pounders Fest`,
                          `Results from ${h.name}`
                        )}
                        className="ml-auto brutal-2 flex items-center gap-1 font-display font-bold uppercase text-[10px] px-2 py-1 active:scale-95 transition-transform"
                        title="Share this heat"
                      >
                        <Share2 size={13} /> Share
                      </button>
                    </div>
                    <div className="space-y-1">
                      {h.leaderboard.map((r) => (
                        <div key={r.rider_id} className="flex items-center gap-2">
                          <span className="font-score text-2xl w-6 text-center text-gray-400">{r.rank}</span>
                          <JerseyBadge jersey={r.jersey} size="sm" />
                          <span className="font-display font-bold uppercase text-sm flex-1 truncate">{r.name}</span>
                          <span className="font-score text-3xl">{fmt(r.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
        </>
      )}

      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-black text-white font-display font-black uppercase tracking-widest px-6 py-3 z-50 text-center" data-testid="results-toast">
          {toast}
        </div>
      )}
    </div>
  );
}
